import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, Subject, take } from 'rxjs';
import { MessageService } from 'primeng/api';
import { SessionTimeoutService } from '@formrails/shared/data-access';
import { CanComponentDeactivate } from '@formrails/form-builder/data-access';
import { UnsavedChangesDialogComponent } from '@formrails/form-builder/ui';
import {
  WorkflowStore,
  WorkflowPersistenceService,
  Workflow,
  WorkflowNode,
  NodePosition,
  nextFreeNodePosition,
  CanvasViewport,
  WorkflowOrientation
} from '@formrails/workflow-designer/data-access';
import {
  WorkflowToolbarComponent,
  WorkflowNodePaletteComponent,
  WorkflowCanvasComponent,
  WorkflowNodePropertiesComponent,
  WorkflowValidationPanelComponent,
  WorkflowSelectorModalComponent,
  NodePaletteSelection,
  NodeMove,
  NodeMeasurement,
  NodeConnect,
  EdgeDelete
} from '@formrails/workflow-designer/ui';

/**
 * Workflow designer feature: composes the toolbar, node palette, canvas and
 * node-properties panel around the WorkflowStore. New nodes are placed in the
 * first free spot on the canvas; positions are UI-only state held in the store.
 */
@Component({
  selector: 'app-workflow-designer',
  standalone: true,
  imports: [
    WorkflowToolbarComponent,
    WorkflowNodePaletteComponent,
    WorkflowCanvasComponent,
    WorkflowNodePropertiesComponent,
    WorkflowValidationPanelComponent,
    WorkflowSelectorModalComponent,
    UnsavedChangesDialogComponent
  ],
  templateUrl: './workflow-designer.component.html',
  styleUrl: './workflow-designer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowDesignerComponent implements OnInit, CanComponentDeactivate {
  private readonly store = inject(WorkflowStore);
  private readonly route = inject(ActivatedRoute);
  private readonly persistence = inject(WorkflowPersistenceService);
  private readonly sessionTimeout = inject(SessionTimeoutService);
  private readonly location = inject(Location);
  /** Root service behind the app-wide <p-toast>. */
  private readonly messageService = inject(MessageService);
  /** Whether the workflow existed server-side when the current save began. */
  private wasPersistedBeforeSave = false;

  // Store selectors exposed to the template
  readonly currentWorkflow = this.store.currentWorkflow;
  readonly nodes = this.store.nodes;
  readonly positions = this.store.positions;
  readonly selectedNodeId = this.store.selectedNodeId;
  readonly selectedNode = this.store.selectedNode;
  readonly workflowName = this.store.workflowName;
  readonly workflowDescription = this.store.workflowDescription;
  readonly nodeCount = this.store.nodeCount;
  readonly validation = this.store.validation;
  readonly hasValidationErrors = this.store.hasValidationErrors;
  readonly orientation = this.store.orientation;

  // Workflow selector
  readonly workflows = this.store.workflows;
  readonly loadingWorkflows = this.store.loadingWorkflows;
  readonly workflowsError = this.store.workflowsError;
  readonly previewNodes = this.store.previewNodes;
  readonly loadingPreview = this.store.loadingPreview;
  readonly previewError = this.store.previewError;
  readonly selectorOpen = signal(false);

  // Unsaved-changes handling, matching the form builder: a dialog rather than
  // window.confirm, and a guard that waits on the user's choice.
  readonly showUnsavedChangesDialog = signal(false);
  readonly unsavedChangesMessage =
    'You have unsaved changes in this workflow. Would you like to save before leaving?';
  /** Drives the toolbar's "Unsaved changes" / "Saved" note. */
  readonly unsavedChanges = computed(() => this.hasUnsavedChanges());
  private pendingAction: (() => void) | null = null;
  private readonly deactivateSubject = new Subject<boolean>();
  /** Set when the dialog's Save is chosen, so the pending action waits for the save to land. */
  private runPendingAfterSave = false;

  private static readonly SESSION_ALIVE_KEY = 'workflow-designer-session-alive';
  private static readonly PROPERTIES_WIDTH_KEY = 'workflow-designer-properties-width';
  private static readonly DEFAULT_PROPERTIES_WIDTH = 300;
  /** Share of the workspace the details panel may take on wide screens. */
  private static readonly MAX_PROPERTIES_SHARE = 0.6;
  /** Palette column width plus the narrowest canvas worth keeping (matches the SCSS grid). */
  private static readonly PALETTE_WIDTH = 220;
  private static readonly MIN_CANVAS_WIDTH = 360;
  private static readonly PROPERTIES_KEYBOARD_STEP = 16;

  readonly propertiesMinWidth = 260;
  /** Width of the node details panel; resizable and remembered per browser. */
  readonly propertiesWidth = signal(this.loadPropertiesWidth());
  private readonly resizingProperties = signal(false);
  readonly isResizingProperties = this.resizingProperties.asReadonly();
  private readonly workflowBody = viewChild.required<ElementRef<HTMLElement>>('workflowBody');
  private propertiesResizeStart: { pointerX: number; width: number } | null = null;

  /**
   * Serialised workflow state as of the last load/save. Compared against the
   * live state to decide whether the unsaved-changes prompts should fire.
   * Null means "no clean baseline" (new workflow) — falls back to a
   * has-any-content check. Positions are excluded: canvas layout is UI-only
   * and never reaches the server today.
   */
  private readonly savedSnapshot = signal<string | null>(null);
  private wasLoading = false;
  private wasSaving = false;

  constructor() {
    // Auto-save the draft to localStorage whenever the structure changes.
    effect(() => {
      this.currentWorkflow();
      this.nodes();
      this.positions();
      untracked(() => this.persistence.saveDraft());
    });

    // A completed load or save establishes the clean dirty-check baseline.
    effect(() => {
      const loading = this.store.loading();
      const saving = this.store.saving();
      const error = this.store.error();
      untracked(() => {
        if (!this.wasSaving && saving) {
          this.wasPersistedBeforeSave = this.store.isPersisted();
        }
        if ((this.wasLoading && !loading && !error) || (this.wasSaving && !saving && !error)) {
          this.savedSnapshot.set(this.captureSnapshot());
        }
        if (this.wasSaving && !saving) {
          this.notifySaveResult(error);
          if (this.runPendingAfterSave) {
            this.runPendingAfterSave = false;
            // A failed save keeps the user where they are, with their work intact.
            if (error) {
              this.pendingAction = null;
              this.deactivateSubject.next(false);
            } else {
              this.runPendingAction();
            }
          }
        }
        this.wasLoading = loading;
        this.wasSaving = saving;
      });
    });
  }

  ngOnInit(): void {
    // Fresh browser session vs refresh: sessionStorage survives refreshes but
    // not tab/browser close. On a genuinely new session, drop stale drafts.
    const isExistingSession =
      sessionStorage.getItem(WorkflowDesignerComponent.SESSION_ALIVE_KEY) === 'true';
    if (!isExistingSession) {
      this.persistence.clearDraftAndRecovery();
    }
    sessionStorage.setItem(WorkflowDesignerComponent.SESSION_ALIVE_KEY, 'true');

    const id = this.route.snapshot.paramMap.get('id');
    const draft = this.persistence.loadDraft();

    if (id) {
      if (draft && draft.workflow.id === id) {
        // The draft holds unsaved edits (and the canvas layout, which the API
        // can't round-trip yet) — prefer it over the server copy.
        this.store.restoreDraft(draft);
        this.savedSnapshot.set(null);
      } else {
        this.store.loadWorkflow(id);
      }
    } else if (this.store.currentWorkflow()) {
      // Explicit "new workflow" navigation already initialised the store —
      // keep it; autosave will replace whatever draft came before.
    } else if (draft && !draft.isPersisted) {
      // Refresh (or post-login recovery) on /workflows/new: the draft is the
      // only copy of this workflow anywhere — restore it.
      this.store.restoreDraft(draft);
    } else {
      this.store.initializeWorkflow('');
    }

    this.persistence.clearSessionRecovery();
  }

  /**
   * Warn the user before closing/refreshing the browser if there are unsaved
   * changes. Shows the browser's native "Leave site?" confirmation dialog.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    // Session expired: local work has been cleared on purpose, so don't
    // block the redirect to login with a "Leave site?" prompt.
    if (this.sessionTimeout.isRedirecting()) return;
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
    }
  }

  /**
   * CanDeactivate guard implementation. Resolves from the unsaved-changes
   * dialog, so navigation waits on the user's choice rather than a
   * window.confirm.
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasUnsavedChanges()) {
      return true;
    }
    this.pendingAction = () => this.deactivateSubject.next(true);
    this.showUnsavedChangesDialog.set(true);
    return this.deactivateSubject.asObservable().pipe(take(1));
  }

  /** Single entry point for anything that would discard unsaved work. */
  private confirmIfUnsavedChanges(action: () => void): void {
    if (this.hasUnsavedChanges()) {
      this.pendingAction = action;
      this.showUnsavedChangesDialog.set(true);
    } else {
      action();
    }
  }

  onUnsavedChangesSave(): void {
    // Unlike the form builder, the pending action here mutates the same store
    // the save reads, so it waits for the save to land rather than racing it.
    this.runPendingAfterSave = true;
    this.showUnsavedChangesDialog.set(false);
    this.onSave();
  }

  onUnsavedChangesDontSave(): void {
    this.showUnsavedChangesDialog.set(false);
    this.runPendingAfterSave = false;
    // Otherwise the draft would restore the discarded work on the next visit.
    this.persistence.clearDraft();
    this.savedSnapshot.set(null);
    this.runPendingAction();
  }

  onUnsavedChangesCancel(): void {
    this.showUnsavedChangesDialog.set(false);
    this.runPendingAfterSave = false;
    this.pendingAction = null;
    // Tell the route guard the user chose to stay.
    this.deactivateSubject.next(false);
  }

  private runPendingAction(): void {
    const action = this.pendingAction;
    this.pendingAction = null;
    action?.();
  }

  /** Serialise the parts of workflow state that round-trip to the server. */
  private captureSnapshot(): string {
    return JSON.stringify({
      workflow: this.currentWorkflow(),
      nodes: this.nodes()
    });
  }

  private hasUnsavedChanges(): boolean {
    const savedSnapshot = this.savedSnapshot();
    if (savedSnapshot !== null) {
      return this.captureSnapshot() !== savedSnapshot;
    }
    const workflow = this.currentWorkflow();
    if (!workflow) {
      return false;
    }
    return (
      this.nodes().length > 0 ||
      !!workflow.name?.trim() ||
      !!workflow.description?.trim()
    );
  }

  onAddNode(selection: NodePaletteSelection): void {
    this.store.addNode({ ...selection, position: this.nextPosition() });
  }

  onSelectNode(id: string): void {
    this.store.selectNode(id || null);
  }

  onMoveNode(move: NodeMove): void {
    this.store.setNodePosition(move.id, move.position);
  }

  onPropertiesResizeStart(event: PointerEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    this.propertiesResizeStart = { pointerX: event.clientX, width: this.propertiesWidth() };
    this.resizingProperties.set(true);
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPropertiesResizeMove(event: PointerEvent): void {
    const start = this.propertiesResizeStart;
    if (!start) return;
    // The handle is on the panel's left edge, so dragging left widens it.
    this.propertiesWidth.set(this.clampPropertiesWidth(start.width + start.pointerX - event.clientX));
  }

  onPropertiesResizeEnd(): void {
    if (!this.propertiesResizeStart) return;
    this.propertiesResizeStart = null;
    this.resizingProperties.set(false);
    this.savePropertiesWidth();
  }

  onPropertiesResizeReset(): void {
    this.propertiesWidth.set(this.clampPropertiesWidth(WorkflowDesignerComponent.DEFAULT_PROPERTIES_WIDTH));
    this.savePropertiesWidth();
  }

  onPropertiesResizeKeydown(event: KeyboardEvent): void {
    const step = WorkflowDesignerComponent.PROPERTIES_KEYBOARD_STEP;
    const delta = event.key === 'ArrowLeft' ? step : event.key === 'ArrowRight' ? -step : 0;
    if (!delta) return;
    event.preventDefault();
    this.propertiesWidth.set(this.clampPropertiesWidth(this.propertiesWidth() + delta));
    this.savePropertiesWidth();
  }

  onMeasureCanvas(viewport: CanvasViewport): void {
    this.store.setCanvasViewport(viewport);
  }

  onMeasureNodes(measurements: NodeMeasurement[]): void {
    this.store.applyMeasuredSizes(measurements);
  }

  onConnectNodes(connect: NodeConnect): void {
    if (connect.fromId === connect.toId) return;
    this.store.connectNodes(connect.fromId, connect.toId, connect.branch);
  }

  onDeleteEdge(edge: EdgeDelete): void {
    this.store.disconnectNodes(edge.fromId, edge.branch);
  }

  onNodeChange(node: WorkflowNode): void {
    this.store.updateNode(node);
  }

  onDeleteNode(id: string): void {
    this.store.deleteNode(id);
  }

  onNameChange(name: string): void {
    this.store.updateWorkflowName(name);
  }

  onDescriptionChange(description: string): void {
    this.store.updateWorkflowDescription(description);
  }

  onOpenSelector(): void {
    this.confirmIfUnsavedChanges(() => {
      this.store.loadWorkflows();
      this.selectorOpen.set(true);
    });
  }

  onCloseSelector(): void {
    this.selectorOpen.set(false);
    this.store.clearWorkflowPreview();
  }

  onPreviewWorkflow(id: string): void {
    this.store.loadWorkflowPreview(id);
  }

  /**
   * Load the chosen workflow in place. The URL is replaced rather than
   * navigated: this component is reused across /workflows/:id, so a param
   * change would leave ngOnInit — and the load with it — unrun.
   */
  onOpenWorkflow(workflow: Workflow): void {
    // Unsaved work was already settled when the selector opened.
    this.onCloseSelector();
    this.persistence.clearDraft();
    this.savedSnapshot.set(null);
    this.store.loadWorkflow(workflow.id);
    this.location.replaceState(`/workflows/${workflow.id}`);
  }

  onCreateNew(): void {
    this.confirmIfUnsavedChanges(() => {
      this.persistence.clearDraft();
      this.store.initializeWorkflow('');
      this.savedSnapshot.set(null);
      this.location.replaceState('/workflows');
    });
  }

  /** Persist the workflow and its nodes via the store. */
  onSave(): void {
    this.store.saveWorkflow();
  }

  private notifySaveResult(error: string | null): void {
    if (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Save Failed',
        detail: error,
        life: 5000
      });
      return;
    }

    const name = this.store.workflowName()?.trim();
    this.messageService.add({
      severity: 'success',
      summary: this.wasPersistedBeforeSave ? 'Workflow Saved' : 'Workflow Created',
      detail: name
        ? `"${name}" has been saved successfully.`
        : 'Your workflow has been saved successfully.',
      life: 3000
    });
  }

  private clampPropertiesWidth(width: number): number {
    const bodyWidth = this.workflowBody().nativeElement.clientWidth;
    const max = Math.max(
      this.propertiesMinWidth,
      Math.min(
        bodyWidth * WorkflowDesignerComponent.MAX_PROPERTIES_SHARE,
        bodyWidth - WorkflowDesignerComponent.PALETTE_WIDTH - WorkflowDesignerComponent.MIN_CANVAS_WIDTH
      )
    );
    return Math.round(Math.min(max, Math.max(this.propertiesMinWidth, width)));
  }

  private loadPropertiesWidth(): number {
    try {
      const stored = Number(localStorage.getItem(WorkflowDesignerComponent.PROPERTIES_WIDTH_KEY));
      return stored >= this.propertiesMinWidth ? stored : WorkflowDesignerComponent.DEFAULT_PROPERTIES_WIDTH;
    } catch {
      return WorkflowDesignerComponent.DEFAULT_PROPERTIES_WIDTH;
    }
  }

  private savePropertiesWidth(): void {
    try {
      localStorage.setItem(
        WorkflowDesignerComponent.PROPERTIES_WIDTH_KEY,
        String(this.propertiesWidth())
      );
    } catch {
      // Storage unavailable (private mode, quota): the width just won't persist.
    }
  }

  onOrientationChange(orientation: WorkflowOrientation): void {
    this.store.setOrientation(orientation);
  }

  /** Place new nodes where they won't land on top of existing cards. */
  private nextPosition(): NodePosition {
    return nextFreeNodePosition(
      this.store.nodes(),
      this.store.positions(),
      this.store.orientation()
    );
  }
}
